import { type DocNode } from '@atlaskit/adf-schema';
import { findFilepaths, isLinkInMatches } from '@atlaskit/editor-common/utils';
import { ADFEncoder } from '@atlaskit/renderer/utils';

import { AiMateMarkdownTransformer } from '../content-transformer/md-tokenizer/markdown-transformer';
import {
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
	ROVO_SKILL_TAG_PATTERN,
} from '../skills';

export type Content = DocNode | string;

export const isAdfContent = (content: Content): content is DocNode => {
	/*
	This try-catch is a necessary guard rail against broken content to prevent higher level
	error boundaries from displaying the error and breaking the experience.
	*/
	try {
		return typeof content !== 'string' && 'type' in content && content.type === 'doc';
	} catch {
		return false;
	}
};

export const appendContent = (
	content: string | DocNode,
	suggestionContent: string,
): string | DocNode => {
	if (isAdfContent(content)) {
		// Ensure the content is an array of nodes
		const updatedContent = Array.isArray(content.content) ? content.content : [content.content];

		// Append the new paragraph node
		updatedContent.push({
			type: 'paragraph',
			content: [
				{
					type: 'text',
					text: suggestionContent,
				},
			],
		});

		// Return the updated DocNode
		return {
			...content,
			content: updatedContent,
		};
	}
	return `${content}\n\n${suggestionContent}`;
};

const pluginConfig = {
	linkify: {
		findFilepaths,
		isLinkInMatches,
	},
	smartLinks: {
		regexStorage: globalThis.localStorage,
	},
};

// Default schema is https://bitbucket.org/atlassian/adf-schema/src/main/packages/adf-schema/src/schema/default-schema.ts
export const adfEncoder: ADFEncoder<string> = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, false),
);

const streamingAdfEncoder = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, true),
);

export const encodeAdf = (
	content: Content,
	streaming: boolean = false,
	isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
	shouldShowAsInline?: boolean,
	isNonAgentMessage?: boolean,
): DocNode => {
	if (isAdfContent(content)) {
		return content;
	}

	if (isExperimentEnabled || isNonAgentMessage) {
		return new ADFEncoder(
			(schema) =>
				new AiMateMarkdownTransformer(
					schema,
					pluginConfig,
					streaming,
					isExperimentEnabled,
					shouldShowAsInline,
					isNonAgentMessage,
				),
		).encode(content);
	} else {
		return streaming ? streamingAdfEncoder.encode(content) : adfEncoder.encode(content);
	}
};

export const encodeSkillPromptToAdf = (content: string): { adf: DocNode; promptLength: number } => {
	const contentNodes: Array<
		| { type: 'text'; text: string }
		| {
				type: 'inlineExtension';
				attrs: { extensionKey: string; extensionType: string; parameters: { slug: string } };
		  }
	> = [];
	let lastIndex = 0;
	let match;
	let promptLength = 0;

	// Find all skill tag matches
	const matches: Array<{ index: number; skillSlug: string; length: number }> = [];
	while ((match = ROVO_SKILL_TAG_PATTERN.exec(content)) !== null) {
		matches.push({
			index: match.index,
			skillSlug: match[1], // The skill name without the slash
			length: match[0].length, // Length of "/skill-name"
		});
	}

	// Process the string segment by segment
	for (let i = 0; i < matches.length; i++) {
		const currentMatch = matches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text before the skill tag (if any)
		if (matchStart > lastIndex) {
			const textBefore = content.substring(lastIndex, matchStart);
			if (textBefore) {
				contentNodes.push({
					type: 'text',
					text: textBefore,
				});
				promptLength += textBefore.length;
			}
		}

		// Add the skill tag as inlineExtension
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.skillSlug,
				},
			},
		});
		promptLength += 1;

		lastIndex = matchEnd;
	}

	// Add remaining text after the last match (if any)
	if (lastIndex < content.length) {
		const textAfter = content.substring(lastIndex);
		if (textAfter) {
			contentNodes.push({
				type: 'text',
				text: textAfter,
			});
			promptLength += textAfter.length;
		}
	}

	// If no matches were found, return the entire string as text
	if (contentNodes.length === 0) {
		contentNodes.push({
			type: 'text',
			text: content,
		});
		promptLength += content.length;
	}

	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: contentNodes,
				},
			],
		},
		promptLength,
	};
};

// Only converts quoted '/slug' (e.g. '/create-release') to skill inlineExtensions.
// Other slashes in the text (e.g. product/feature) are left as plain text.
// When content contains backtick placeholders (e.g. `project/product`), strips them and returns
// placeholderRange so the link typeahead can open.
// NOTE: Please try not to add more placeholder patterns.
const QUOTED_SKILL_TAG_PATTERN = /'\/([a-zA-Z0-9-]+)'/g;
export const PROMPT_PLACEHOLDER_CHAR = '`';

export type QuotedSkillTagsAdfResult = {
	adf: DocNode;
	promptLength: number;
	placeholderRange?: { start: number; end: number; label: string };
};

// Strips backticks and returns the placeholder range to be used for link typeahead.
function stripBackticksAndGetPlaceholderRange(
	segment: string,
	currentContentIndex: number,
): { stripped: string; placeholderRange?: { start: number; end: number; label: string } } {
	const firstBacktick = segment.indexOf(PROMPT_PLACEHOLDER_CHAR);
	const lastBacktick = segment.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
	if (firstBacktick === -1 || lastBacktick === -1 || firstBacktick === lastBacktick) {
		return { stripped: segment };
	}
	const label = segment.slice(firstBacktick + 1, lastBacktick);
	const stripped = segment.replace(new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'), '');
	const start = currentContentIndex + firstBacktick + 1;
	return {
		stripped,
		placeholderRange: { start, end: start + label.length, label },
	};
}

export const encodeQuotedSkillTagsToAdf = (
	content: string,
	skillMetadata?: Record<string, Record<string, unknown>>,
): QuotedSkillTagsAdfResult => {
	const contentNodes: Array<
		| { type: 'text'; text: string }
		| {
				type: 'inlineExtension';
				attrs: {
					extensionKey: string;
					extensionType: string;
					parameters: { slug: string; [key: string]: unknown };
				};
		  }
	> = [];
	let lastIndex = 0;
	let match;
	let promptLength = 0;
	// Track the placeholder range for the skill tag so we can also handle cursor selection for link typeahead in the prompt
	let placeholderRange: QuotedSkillTagsAdfResult['placeholderRange'];

	// Find all skill tag matches
	const matches: Array<{ index: number; skillSlug: string; length: number }> = [];
	while ((match = QUOTED_SKILL_TAG_PATTERN.exec(content)) !== null) {
		matches.push({
			index: match.index,
			skillSlug: match[1],
			length: match[0].length,
		});
	}

	// Process the string segment by segment
	for (let i = 0; i < matches.length; i++) {
		const currentMatch = matches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text before the skill tag (if any) and check for backtick placeholders
		if (matchStart > lastIndex) {
			const textSegment = content.substring(lastIndex, matchStart);
			if (textSegment) {
				const { stripped, placeholderRange: range } = stripBackticksAndGetPlaceholderRange(
					textSegment,
					promptLength,
				);
				if (range) {
					placeholderRange = range;
				}
				contentNodes.push({ type: 'text', text: stripped });
				promptLength += stripped.length;
			}
		}

		// Add the skill tag as inlineExtension with skill metadata
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.skillSlug,
					...skillMetadata?.[currentMatch.skillSlug],
				},
			},
		});
		promptLength += 1;
		lastIndex = matchEnd;
	}

	// Add remaining text after the last match (if any) and check for backtick placeholders
	if (lastIndex < content.length) {
		const textSegment = content.substring(lastIndex);
		if (textSegment) {
			const { stripped, placeholderRange: range } = stripBackticksAndGetPlaceholderRange(
				textSegment,
				promptLength,
			);
			if (range) {
				placeholderRange = range;
			}
			contentNodes.push({ type: 'text', text: stripped });
			promptLength += stripped.length;
		}
	}

	// If no matches were found, return the entire string as text and check for backtick placeholders
	if (contentNodes.length === 0) {
		const { stripped, placeholderRange: range } = stripBackticksAndGetPlaceholderRange(content, 0);
		if (range) {
			placeholderRange = range;
		}
		contentNodes.push({ type: 'text', text: stripped });
		promptLength = stripped.length;
	}

	// Return the ADF document, prompt length, and placeholder range
	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: contentNodes,
				},
			],
		},
		promptLength,
		...(placeholderRange && { placeholderRange }),
	};
};

type ContentNode =
	| { type: 'text'; text: string }
	| {
			type: 'inlineExtension';
			attrs: {
				extensionKey: string;
				extensionType: string;
				parameters: { slug: string; [key: string]: unknown };
			};
	  };

type DetectedSkill = { index: number; slug: string; length: number };

// encodeAdf removes leading spaces, so prepend a text node with the segment's leading spaces to preserve them.
const preserveLeadingSpaces = (textSegment: string, nodes: ContentNode[]): ContentNode[] => {
	const leading = textSegment.match(/^\s*/)?.[0] ?? '';
	if (leading.length === 0) {
		return nodes;
	}
	return [{ type: 'text', text: leading }, ...nodes];
};

// Collect all content nodes from all top-level blocks, since encodeAdf can return multiple paragraphs for e.g. "line1\n\nline2".
const getAllNodesFromSegmentAdf = (segmentAdf: DocNode, fallbackSegment: string): ContentNode[] => {
	const blocks = segmentAdf.content ?? [];
	if (blocks.length === 0) {
		return [{ type: 'text' as const, text: fallbackSegment }];
	}
	const nodes = blocks.flatMap((block) => (block as { content?: ContentNode[] }).content ?? []);
	return nodes.length > 0 ? nodes : [{ type: 'text' as const, text: fallbackSegment }];
};

const detectSkills = (
	content: string,
	skillMetadata?: Record<string, Record<string, unknown>>,
): DetectedSkill[] => {
	if (!skillMetadata) {
		return [];
	}
	const regex = new RegExp(ROVO_SKILL_TAG_PATTERN.source, 'g');
	const matches = [...content.matchAll(regex)];
	if (matches.length === 0) {
		return [];
	}
	return matches
		.filter((m) => m[1] in skillMetadata)
		.map((m) => ({
			index: m.index ?? 0,
			slug: m[1],
			length: m[0].length,
		}));
};

export const encodePromptToAdf = (
	content: string,
	skillMetadata?: Record<string, Record<string, unknown>>,
): QuotedSkillTagsAdfResult => {
	const skillMatches = detectSkills(content, skillMetadata);
	if (skillMatches.length === 0) {
		const { placeholderRange } = getPlaceholderRange(content, 0);
		return {
			adf: encodeAdf(content),
			promptLength: content.length,
			...(placeholderRange && { placeholderRange }),
		};
	}

	return encodePromptWithSkills(content, skillMatches, skillMetadata);
};

const encodePromptWithSkills = (
	content: string,
	skillMatches: DetectedSkill[],
	skillMetadata?: Record<string, Record<string, unknown>>,
): QuotedSkillTagsAdfResult => {
	const contentNodes: ContentNode[] = [];
	let lastIndex = 0;
	let promptLength = 0;
	let placeholderRange: QuotedSkillTagsAdfResult['placeholderRange'];

	for (let i = 0; i < skillMatches.length; i++) {
		const currentMatch = skillMatches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text and placeholder range before the skill tag (if any)
		if (matchStart > lastIndex) {
			const textSegment = content.substring(lastIndex, matchStart);
			if (textSegment) {
				const { placeholderRange: range } = getPlaceholderRange(textSegment, promptLength);
				if (range) {
					placeholderRange = range;
				}
				const segmentAdf = encodeAdf(textSegment);
				const nodes = getAllNodesFromSegmentAdf(segmentAdf, textSegment);
				contentNodes.push(...preserveLeadingSpaces(textSegment, nodes));
				promptLength += textSegment.length;
			}
		}

		// Add the skill tag as inlineExtension
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.slug,
					...skillMetadata?.[currentMatch.slug],
				},
			},
		});
		promptLength += 1;
		lastIndex = matchEnd;
	}

	// Add remaining text and placeholder range after the last match (if any)
	if (lastIndex < content.length) {
		const textSegment = content.substring(lastIndex);
		if (textSegment) {
			const { placeholderRange: range } = getPlaceholderRange(textSegment, promptLength);
			if (range) {
				placeholderRange = range;
			}
			const segmentAdf = encodeAdf(textSegment);
			const nodes = getAllNodesFromSegmentAdf(segmentAdf, textSegment);
			contentNodes.push(...preserveLeadingSpaces(textSegment, nodes));
			promptLength += textSegment.length;
		}
	}
	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [{ type: 'paragraph', content: contentNodes }],
		},
		promptLength,
		...(placeholderRange && { placeholderRange }),
	};
};

// Returns the placeholder range for link typeahead when content has `label` backticks.
const getPlaceholderRange = (
	segment: string,
	currentContentIndex: number,
): { placeholderRange?: { start: number; end: number; label: string } } => {
	const firstBacktick = segment.indexOf(PROMPT_PLACEHOLDER_CHAR);
	const lastBacktick = segment.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
	if (firstBacktick !== -1 && lastBacktick !== -1 && firstBacktick !== lastBacktick) {
		const label = segment.slice(firstBacktick + 1, lastBacktick);
		const start = currentContentIndex + firstBacktick + 1;
		return {
			placeholderRange: { start, end: start + label.length, label },
		};
	}
	return {};
};
